import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { database, storage } from 'helpers/firebase';
import Collapse from '@kunukn/react-collapse';
import CreatableSelect from 'react-select/creatable';
import Heading from 'components/atoms/Heading';
import Paragraph from 'components/atoms/Paragraph';
import { getSizeLabel } from 'helpers/functions';
import { customStyles } from 'helpers/constants';
import { ReactComponent as ArrowRight } from 'assets/icons/arrow_right.svg';

const StyledHeading = styled(Heading)`
  font-size: 24pt;
  color: ${({ theme }) => theme.textSecondary};
  pointer-events: ${({ noInteraction }) => (noInteraction ? 'none' : 'all')};
`;

const Info = styled(Paragraph)`
  color: ${({ theme }) => theme.textSecondary};
  margin-top: 15px;
`;

const Red = styled.span`
  color: ${({ theme }) => theme.red};
`;

const DropdownArea = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  height: 100% !important;
  width: 100%;
`;

const InvisibleInput = styled.input`
  visibility: hidden;
  height: 0px;
  width: 0px;
`;

const LabelLink = styled.label`
  color: ${({ theme }) => theme.blue};
  cursor: pointer;

  :hover {
    text-decoration: underline;
  }
`;

const CollapseWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  min-height: 48px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.gray4};
  margin-bottom: 25px;
`;

const CollapseHeader = styled.button`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  width: 100%;
  padding: 0 5px 0 15px;
  border: none;
  overflow: hidden;
  background-color: transparent;
  cursor: pointer;
  outline: none;
`;

const StyledCollapse = styled(Collapse)`
  padding: 0 15px;
  transition: height 0.28s cubic-bezier(0.4, 0, 0.2, 1);
`;

const Item = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  width: 100%;
`;

const StyledIcon = styled(ArrowRight)`
  transform: rotate(${({ isopen }) => (isopen ? '90deg' : '-90deg')});
  fill: ${({ theme }) => theme.text};
  height: 32px;
  width: 32px;
  transition: transform 0.2s ease-in-out, color 0.1s ease-in-out;
`;

const Row = styled.div`
  display: flex;
  flex-flow: row nowrap;
  width: ${({ noFullWidth }) => (noFullWidth ? 'auto' : '100%')};
`;

const Column = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

const Button = styled.button`
  width: 230px;
  height: 32px;
  background-color: ${({ theme }) => theme.blue};
  border: none;
  border-radius: 10px;
  margin-top: 25px;
  margin-bottom: 25px;
  cursor: pointer;
  outline: none;

  :hover {
    background-color: ${({ theme }) => theme.blueHover};
  }

  :disabled {
    cursor: default;
    opacity: 0.38;
  }
`;

const ProgressBar = styled.div`
  position: relative;
  height: 32px;
  width: 300px;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.gray4};
  overflow: hidden;
  margin-bottom: 25px;

  ::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${({ width }) => `${width}%`};
    background-color: ${({ theme }) => theme.green};
    transition: width 0.3s ease-in-out;
  }

  ${Paragraph} {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

function Upload() {
  const [isDrop, setIsDrop] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [currentFiles, setCurrentFiles] = useState([]);
  const [errorFiles, setErrorFiles] = useState([]);
  const [isFilesOpen, setIsFilesOpen] = useState(false);
  const [isErrorFilesOpen, setIsErrorFilesOpen] = useState(false);
  const [filesSize, setFilesSize] = useState('0B');
  const [errorFilesSize, setErrorFilesSize] = useState('0B');
  const [options, setOptions] = useState([
    {
      value: 'all',
      label: 'Wszystkie pliki',
    },
  ]);
  const [optionValue, setOptionValue] = useState({
    value: 'all',
    label: 'Wszystkie pliki',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);
  const [isFinished, setIsFinished] = useState(false);

  useEffect(() => {
    database.ref('/folders').on('value', (snapshot) => {
      const folders = [];
      if (snapshot.val()) {
        Object.keys(snapshot.val()).forEach((key) => {
          folders.push({ label: snapshot.val()[key], value: key });
        });
      }

      setOptions((prevState) => [...prevState, ...folders]);
    });
  }, []);

  useEffect(() => {
    let sum = 0;

    currentFiles.forEach(({ size }) => {
      sum += size;
    });

    setFilesSize(getSizeLabel(sum));
  }, [currentFiles]);

  useEffect(() => {
    let sum = 0;

    errorFiles.forEach(({ size }) => {
      sum += size;
    });

    setErrorFilesSize(getSizeLabel(sum));
  }, [errorFiles]);

  useEffect(() => {
    if (optionValue) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [optionValue]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDrop(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDrop(false);
  };

  const validateFiles = (files) => {
    files.forEach((file) => {
      if (file.size < 50 * 1024 * 1024) {
        setCurrentFiles((prevState) => [...prevState, file]);
      } else {
        setErrorFiles((prevState) => [...prevState, file]);
      }
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDrop(false);
    setIsUploaded(true);
    const files = [];

    if (e.dataTransfer.items) {
      for (let i = 0; i < e.dataTransfer.items.length; i += 1) {
        if (e.dataTransfer.items[i].kind === 'file') {
          const file = e.dataTransfer.items[i].getAsFile();

          files.push(file);
        }
      }
    }

    validateFiles(files);
  };

  const handleUpload = (e) => {
    if (e.target.files.length) {
      setIsDrop(false);
      setIsUploaded(true);

      const files = Array.from(e.target.files);
      validateFiles(files);
    }
  };

  const handleChange = (newValue) => {
    setOptionValue(newValue);
  };

  const handleCreateFolder = (inputValue) => {
    setIsLoading(true);

    const pushRef = database.ref('/folders').push();

    pushRef.set(inputValue, () => {
      setOptionValue({
        label: inputValue,
        value: pushRef.key,
      });

      setIsLoading(false);
    });
  };

  const handleStart = () => {
    setIsDisabled(true);
    setIsLoading(true);

    const databaseRef = database.ref('/files');

    let index = 1;
    const maxIndex = currentFiles.length;

    currentFiles.forEach((file) => {
      setProgress(0);

      const { name, size, lastModified, type } = file;
      const fileObj = {
        name,
        size,
        lastModified,
        type,
        uploaded: new Date().getTime(),
        folder: optionValue.value,
      };

      const newFileRef = databaseRef.push();

      const uploadTask = storage.ref(`files/${newFileRef.key}`).put(file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const p = ((snapshot.bytesTransferred / snapshot.totalBytes) * 100).toFixed(2);

          setProgress(p);
        },
        () => {},
        () => {
          newFileRef.set(fileObj, () => {
            setOverallProgress(((index / currentFiles.length) * 100).toFixed(2));
            if (index === maxIndex) {
              setIsFinished(true);
            } else {
              index += 1;
            }
          });
        },
      );
    });
  };

  return isUploaded ? (
    <Column>
      {currentFiles.length ? (
        <CollapseWrapper>
          <CollapseHeader type="button" onClick={() => setIsFilesOpen((prevState) => !prevState)}>
            <Heading>Przesyłane pliki - {filesSize}</Heading>
            <Row noFullWidth>
              <Heading>{currentFiles.length}</Heading>
              <StyledIcon isopen={isFilesOpen} />
            </Row>
          </CollapseHeader>
          <StyledCollapse isOpen={isFilesOpen}>
            {currentFiles.map(({ name, size }, i) => (
              <Item key={i.toString()}>
                <Paragraph>{name}</Paragraph>
                <Paragraph>{getSizeLabel(size)}</Paragraph>
              </Item>
            ))}
          </StyledCollapse>
        </CollapseWrapper>
      ) : null}

      {errorFiles.length ? (
        <CollapseWrapper>
          <CollapseHeader
            type="button"
            onClick={() => setIsErrorFilesOpen((prevState) => !prevState)}
          >
            <Heading>
              <Red>Odrzucone pliki - {errorFilesSize}</Red>
            </Heading>
            <Row noFullWidth>
              <Heading>{errorFiles.length}</Heading>
              <StyledIcon isopen={isErrorFilesOpen} />
            </Row>
          </CollapseHeader>
          <StyledCollapse isOpen={isErrorFilesOpen}>
            {errorFiles.map(({ name, size }, i) => (
              <Item key={i.toString()}>
                <Paragraph>{name}</Paragraph>
                <Paragraph>{getSizeLabel(size)}</Paragraph>
              </Item>
            ))}
          </StyledCollapse>
        </CollapseWrapper>
      ) : null}
      {currentFiles.length ? (
        <>
          <Heading>Wybierz folder</Heading>
          <CreatableSelect
            isClearable
            isLoading={isLoading}
            isDisabled={isLoading}
            onChange={handleChange}
            onCreateOption={handleCreateFolder}
            options={options}
            value={optionValue}
            styles={customStyles}
            placeholder="Szukaj..."
            formatCreateLabel={(val) => `Stwórz folder o nazwie "${val}"`}
          />
          <Paragraph secondary>Wszystkie przesyłane pliki trafią do tego folderu</Paragraph>

          <Button type="button" disabled={isDisabled} onClick={handleStart}>
            <Paragraph>Rozpocznij przesyłanie</Paragraph>
          </Button>

          <Paragraph>Postęp przesyłania pojedyńczego pliku</Paragraph>
          <ProgressBar width={progress}>
            <Paragraph>{progress}%</Paragraph>
          </ProgressBar>

          <Paragraph>Postęp całkowity</Paragraph>
          <ProgressBar width={overallProgress}>
            <Paragraph>{overallProgress}%</Paragraph>
          </ProgressBar>

          {isFinished ? (
            <>
              <Heading>Zakończono, możesz już zmienić okno</Heading>
            </>
          ) : null}
        </>
      ) : null}
    </Column>
  ) : (
    <DropdownArea onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
      {isDrop ? (
        <StyledHeading noInteraction>Upuść tutaj</StyledHeading>
      ) : (
        <>
          <StyledHeading>
            Upuść tutaj pliki lub{' '}
            <LabelLink>
              dodaj je ręcznie
              <InvisibleInput type="file" multiple onChange={handleUpload} />
            </LabelLink>
          </StyledHeading>
          <Info>
            Maksymalna waga pliku to <Red>50MB</Red>.
          </Info>
        </>
      )}
    </DropdownArea>
  );
}

export default Upload;
